'use client';

import { useCallback, useRef } from 'react';
import { Box, Paper, Stack, SxProps } from '@mui/material';
import { NavigationListener } from '@peerless/ui-navigation';

export type NavMenuProps = React.PropsWithChildren<{
  control: React.ReactNode;
}>;

const style: SxProps = {
  height: '100%',
  position: 'relative',

  '& .menu': {
    position: 'absolute',
    backgroundColor: 'gray.light',
    top: '100%',
    left: 0,
    px: 2,
    py: 2,
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.3s ease, visibility 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 10px 8px -8px rgba(0, 0, 0, 0.4)',
  },

  '&:hover .menu, &:focus-within .menu': {
    visibility: 'visible',
    opacity: 1,
  },
};

export function NavMenu({ control, children }: NavMenuProps) {
  const container = useRef<HTMLDivElement>(null);

  const unfocus = useCallback(() => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.tagName !== 'BODY'
    ) {
      // blur the link to remove focus ring and allow the menu to hide once it has lost its hover state
      document.activeElement.blur();
    }
  }, []);

  const onClose = useCallback(() => {
    unfocus();

    if (container.current) {
      // disable pointer events on the menu to disable hover state and allow it to hide
      const element = container.current;
      const menu = element.querySelector<HTMLElement>('.menu');
      if (menu) {
        menu.style.pointerEvents = 'none';
        // re-enable pointer events once the visibility transition has ended
        menu.addEventListener('transitionend', () => (menu.style.pointerEvents = 'auto'), {
          once: true,
        });
      }
    }
  }, [unfocus]);

  return (
    <Box ref={container} sx={style} onMouseEnter={unfocus}>
      {control}
      <NavigationListener onNavigate={onClose} />
      <Paper className="menu" square role="menu" elevation={4}>
        <Stack direction="row" spacing={4}>
          {children}
        </Stack>
      </Paper>
    </Box>
  );
}
